import { Link, useNavigation, useSearchParams } from '@remix-run/react';
import type { ProfessionalTileProps, AvatarProps } from '@archipro-design/aria';
import { pxToRem } from '@archipro-design/aria';
import { Flex } from '@archipro-design/aria';
import { useStyles, Header, Button, Grid } from '@archipro-design/aria';
import * as S from './FeaturedProfessionals.style';
import { getProfessionalTile } from '@modules/professionals-category/util/get-professional-tile';
import type { ProfessionalTileViewModel } from '@modules/professionals-category/type';
import { useSaveToDesignBoard } from '~/modules/design-board/hook';
import SaveToDesignBoard from '~/modules/design-board/component/SaveToDesignBoard';
import { useTracker } from '@archipro-website/tracker';
import { MEDIA_CONIDTION_SIZES_LARGE_SCREEN } from '~/modules/images/config/sizes-constant';
import ProfessionalTile from '~/modules/tile/component/professional-tile/ProfessionalImpressionTile';
import { getImagesSizes } from '~/utils/image-sizes-utils';
import { useTileHotButtons } from '~/modules/root';

export interface FeaturedProfessionalsData {
    Title: string;
    FeaturedProfessionals: {
        pageInfo: {
            totalCount: number;
        };
        edges: {
            node: ProfessionalTileViewModel;
        }[];
    };
}

interface FeaturedProfessionalsProps {
    data: FeaturedProfessionalsData;
}

const ROW_SIZE = 4;
export const DEFAULT_TILE_LIMIT = ROW_SIZE;
export const SHOW_ALL_PARAM = 'featured-professionals-show-all';

const FeaturedProfessionals = ({
    data: { Title, FeaturedProfessionals },
}: FeaturedProfessionalsProps) => {
    const { css } = useStyles();
    const [searchParams] = useSearchParams();
    const navigation = useNavigation();
    const { favouriteItem } = useSaveToDesignBoard('ProfessionalPin');
    const tracker = useTracker();

    const {
        getTileTopActions,
        getTileAdminCaption,
        hotButtonModal,
        appDisplayMode,
    } = useTileHotButtons({
        itemType: 'professional',
        itemIDs: FeaturedProfessionals.edges.map(
            ({ node: professional }) => professional.ID
        ),
    });

    const { mobile: isMobile } = appDisplayMode;

    const { edges, pageInfo } = FeaturedProfessionals;
    if (!edges?.length) return null;

    const totalCount = pageInfo.totalCount ?? 0;

    const showingAll = !!searchParams.get(SHOW_ALL_PARAM);
    const nextSearchParams = new URLSearchParams(navigation.location?.search);
    const featuredShowAll = nextSearchParams.get(SHOW_ALL_PARAM) === '1';
    const isLoading = navigation.state === 'loading' && featuredShowAll;

    const imageSizes = isMobile
        ? '100vw'
        : `${MEDIA_CONIDTION_SIZES_LARGE_SCREEN} 33vw, 50vw`;

    return (
        <div className={css(S.ProfessionalsWrapper)}>
            <Header variant={'03'}>
                Professionals used in
                <br />
                {Title}
            </Header>
            <Grid columns={ROW_SIZE}>
                {edges.map(({ node: professional }, idx) => {
                    const final = getProfessionalTile(professional, (link) => ({
                        as: Link,
                        to: link,
                    })) as ProfessionalTileProps;

                    const topActions = getTileTopActions({
                        item: professional,
                    });
                    const adminCaption = getTileAdminCaption(professional.ID);

                    const cutOff = !showingAll && idx + 1 > ROW_SIZE;
                    return (
                        <Link
                            key={`featured-professional-tile-${professional.ID}`}
                            className={css(cutOff ? S.TileCutOff : S.Tile)}
                            to={professional.link}
                            prefetch={'intent'}
                        >
                            <ProfessionalTile
                                title={final.title}
                                image={[
                                    {
                                        src: professional.image || '',
                                        width: '100%',
                                        height: '100%',
                                        size: imageSizes,
                                        alt:
                                            typeof final === 'object'
                                                ? (final.title as string)
                                                : '',
                                        sizes: getImagesSizes({
                                            desktop: 595,
                                            mobile: 378,
                                        }),
                                    },
                                ]}
                                logo={{
                                    image: {
                                        src:
                                            typeof final.logo === 'object'
                                                ? ((
                                                      final.logo as unknown as AvatarProps
                                                  ).image as string)
                                                : '',
                                        alt: `${final.title} professional logo`,
                                        loading: 'lazy',
                                    },
                                    ...(typeof final.logo === 'object' && {
                                        variables: (
                                            final.logo as unknown as AvatarProps
                                        ).variables,
                                    }),
                                }}
                                category={final.category}
                                memberSince={final.memberSince}
                                variant={'04'}
                                size={'medium'}
                                hideFavourite={cutOff}
                                onFavourite={(e) =>
                                    favouriteItem.onClick(e, professional.ID)
                                }
                                variables={{
                                    tilePadding: pxToRem(10),
                                }}
                                isFavourited={favouriteItem.favourited(
                                    professional.ID
                                )}
                                onClick={() => {
                                    tracker.log(
                                        'ProfessionalUsedProjectClick',
                                        {
                                            url: new URL(window.location.href),
                                            targetTracker: 'archiproTracker',
                                        }
                                    );
                                }}
                                impression={{
                                    professionalID: professional.ID,
                                    itemID: professional.ID,
                                    type: 'LibraryItem_Directory',
                                }}
                                topActions={topActions}
                                adminCaption={adminCaption}
                            />
                        </Link>
                    );
                })}
            </Grid>

            {!showingAll && totalCount > ROW_SIZE && (
                <Flex hAlign="start">
                    <Button
                        as={Link}
                        to={`?${SHOW_ALL_PARAM}=1`}
                        preventScrollReset={true}
                        loading={isLoading}
                        prefetch="intent"
                        outlined={true}
                        className={css(S.LoadMoreButton)}
                        size={16}
                        style={{
                            textTransform: isLoading ? 'unset' : 'uppercase',
                        }}
                        onClick={() => {
                            tracker.log('ProjectProfessionalExpandClick', {
                                url: new URL(window.location.href),
                                targetTracker: 'archiproTracker',
                            });
                        }}
                    >
                        {!isLoading ? `View more` : 'Loading...'}
                    </Button>
                </Flex>
            )}

            {favouriteItem.target && <SaveToDesignBoard {...favouriteItem} />}
            {hotButtonModal}
        </div>
    );
};

export default FeaturedProfessionals;
